import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePanel } from 'contexts/panelContext';
import { ProgressPairSteps } from 'hooks/useProgressSteps';
import { useProgramTranslation } from 'hooks/useProgramTranslation';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { Icon, IconSvg } from 'components/_new/Icon';
import './ProgressSteps.scss';

type MenteePairQuickLinksProps = {
	pairSteps: ProgressPairSteps;
	mini?: boolean;
};

const MenteePairQuickLinks = ({ pairSteps, mini }: MenteePairQuickLinksProps) => {
	const { pairId, label, secondStage, activeStep, steps } = pairSteps;
	const stepsLength = steps?.length || 0;

	const { t } = useTranslation();
	const { tProgram } = useProgramTranslation();
	const navigate = useNavigate();
	const { panelScopePath, todosData, panelType } = usePanel();

	const pairTodos =
		todosData.pair.find((pairTodo) => pairTodo.items.some((item) => item.variables?.pairId === pairId))?.items ||
		[];

	const hasSessionAlert = pairTodos.some((todo) => todo.type === 'arrange_first_session_date');
	const hasContractAlert = pairTodos.some((todo) => todo.type === 'contract_initial_sign_required');
	const hasMenteePlanAlert = pairTodos.some((todo) => todo.type === 'contract_goals_sign_required');
	const hasFinalReportAlert = pairTodos.some((todo) => todo.type === 'final_report_mentor');
	const hasEvaluationSurveyRequiredAlert = pairTodos.some((todo) => todo.type === 'evaluation_survey_required');
	const isReportDownloaded = pairTodos.some(
		(todo) => todo.type === 'final_report_mentee' && todo.variables?.isReportDownloaded
	);
	const hasProgressStepsAlert = stepsLength >= 5 ? activeStep >= stepsLength - 1 && !isReportDownloaded : false;

	if (mini) {
		return (
			<div className="mentee-pair-quick-links">
				<Box variant="white-bordered" slim full>
					<BoxSection horizontal header={label}>
						<div className="w-full flex flex-row flex-wrap gap-2 md:gap-4">
							<Button
								renderBefore={
									hasSessionAlert ? (
										<Icon
											name="exclamation-circle"
											className="hidden sm:flex text-md text-primary"
										/>
									) : undefined
								}
								icon={() => <IconSvg name="papers" />}
								iconClassName="icon-container icon-container-rm text-primary"
								label={tProgram('userPanel.dashboard.quickLinks.mentoringSessions')}
								className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
								variant={hasSessionAlert ? 'new-purple' : 'transparent'}
								onClick={() => {
									navigate({ pathname: `${panelScopePath}/pair/${pairId}/session` });
								}}
								noPad
							/>

							{!secondStage ? (
								<Button
									renderBefore={
										hasContractAlert ? (
											<Icon
												name="exclamation-circle"
												className="hidden sm:flex text-md text-primary"
											/>
										) : undefined
									}
									icon={() => <IconSvg name="pencil-sign-square" />}
									iconClassName="icon-container icon-container-rm text-primary"
									label={tProgram('userPanel.dashboard.quickLinks.mentoringContract')}
									className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
									variant={hasContractAlert ? 'new-purple' : 'transparent'}
									onClick={() => {
										navigate({ pathname: `${panelScopePath}/pair/${pairId}/contract` });
									}}
									noPad
								/>
							) : null}

							<Button
								renderBefore={
									hasMenteePlanAlert ? (
										<Icon
											name="exclamation-circle"
											className="hidden sm:flex text-md text-primary"
										/>
									) : undefined
								}
								icon={() => <IconSvg name="text-write-square" />}
								iconClassName="icon-container icon-container-rm text-primary"
								label={tProgram('userPanel.dashboard.quickLinks.menteeDevelopmentPlan')}
								className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
								variant={hasMenteePlanAlert ? 'new-purple' : 'transparent'}
								onClick={() => {
									navigate({ pathname: `${panelScopePath}/pair/${pairId}/mentee-plan` });
								}}
								noPad
							/>
							{!secondStage ? (
								<Button
									icon={() => <IconSvg name="communication" />}
									iconClassName="icon-container icon-container-rm text-primary"
									label={t('userPanel.dashboard.quickLinks.menteeDetails')}
									className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
									variant="transparent"
									onClick={() => {
										navigate({ pathname: `${panelScopePath}/pair/${pairId}/pair-details` });
									}}
									noPad
								/>
							) : null}
							{secondStage ? (
								<>
									{panelType === 'mentor' ? (
										<Button
											renderBefore={
												hasFinalReportAlert ? (
													<Icon
														name="exclamation-circle"
														className="hidden sm:flex text-md text-primary"
													/>
												) : undefined
											}
											icon={() => <IconSvg name="checklist-square" />}
											iconClassName="icon-container icon-container-rm text-primary"
											label={t('userPanel.dashboard.quickLinks.finalReport')}
											className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
											variant="transparent"
											onClick={() => {
												navigate({ pathname: `${panelScopePath}/pair/${pairId}/final-report` });
											}}
											noPad
										/>
									) : null}
									{panelType === 'mentee' ? (
										<Button
											renderBefore={
												hasEvaluationSurveyRequiredAlert ? (
													<Icon
														name="exclamation-circle"
														className="hidden sm:flex text-md text-primary"
													/>
												) : undefined
											}
											icon={() => <IconSvg name="arrow-circle" />}
											iconClassName="icon-container icon-container-rm text-primary"
											label={t('userPanel.dashboard.quickLinks.goalsEvaluation')}
											className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
											variant="transparent"
											onClick={() => {
												navigate({
													pathname: `${panelScopePath}/pair/${pairId}/session/last/evaluation-mentee`,
													hash: '#content',
												});
											}}
											noPad
										/>
									) : null}
									<Button
										renderBefore={
											hasProgressStepsAlert ? (
												<Icon
													name="exclamation-circle"
													className="hidden sm:flex text-md text-primary"
												/>
											) : undefined
										}
										icon={() => <IconSvg name="file-check" />}
										iconClassName="icon-container icon-container-rm text-primary"
										label={t('userPanel.dashboard.quickLinks.processSummary')}
										className="flex-1-mhalf p-1 sm:p-2 gap-1 sm:gap-2 justify-content-start text-left"
										variant="transparent"
										onClick={() => {
											navigate({
												pathname: `${panelScopePath}/pair/${pairId}/process-summary`,
											});
										}}
										noPad
									/>
								</>
							) : null}
						</div>
					</BoxSection>
				</Box>
			</div>
		);
	}

	return (
		<div className="mentee-pair-quick-links flex flex-row flex-wrap gap-2 md:gap-4">
			<Link to={{ pathname: `${panelScopePath}/pair/${pairId}/session` }} className="flex-1-mhalf">
				<Box
					className="relative h-full"
					variant={hasSessionAlert ? 'white-rose-bordered' : 'white-bordered'}
					slim
					full
					hoverable
				>
					<BoxSection contentClassName="justify-content-center align-items-center text-center">
						<span className="icon-container icon-container-lg">
							<IconSvg name="papers" className="text-rose" />
						</span>
						<span>{tProgram('userPanel.dashboard.quickLinks.mentoringSessions')}</span>
					</BoxSection>
					{hasSessionAlert ? (
						<div className="box-warning-icon">
							<Icon name="exclamation-circle" className="warning-icon-color" />
						</div>
					) : null}
				</Box>
			</Link>
			{!secondStage ? (
				<Link to={{ pathname: `${panelScopePath}/pair/${pairId}/contract` }} className="flex-1-mhalf">
					<Box
						className="relative h-full"
						variant={hasContractAlert ? 'white-rose-bordered' : 'white-bordered'}
						slim
						full
						hoverable
					>
						<BoxSection contentClassName="justify-content-center align-items-center text-center">
							<span className="icon-container icon-container-lg">
								<IconSvg name="pencil-sign-square" className="text-primary" />
							</span>
							<span>{tProgram('userPanel.dashboard.quickLinks.mentoringContract')}</span>
						</BoxSection>
						{hasContractAlert ? (
							<div className="box-warning-icon">
								<Icon name="exclamation-circle" className="warning-icon-color" />
							</div>
						) : null}
					</Box>
				</Link>
			) : null}
			<Link to={{ pathname: `${panelScopePath}/pair/${pairId}/mentee-plan` }} className="flex-1-mhalf">
				<Box
					className="relative h-full"
					variant={hasMenteePlanAlert ? 'white-rose-bordered' : 'white-bordered'}
					slim
					full
					hoverable
				>
					<BoxSection contentClassName="justify-content-center align-items-center text-center">
						<span className="icon-container icon-container-lg">
							<IconSvg name="text-write-square" className="text-primary" />
						</span>
						<span>{tProgram('userPanel.dashboard.quickLinks.menteeDevelopmentPlan')}</span>
					</BoxSection>
					{hasMenteePlanAlert ? (
						<div className="box-warning-icon">
							<Icon name="exclamation-circle" className="warning-icon-color" />
						</div>
					) : null}
				</Box>
			</Link>
			{!secondStage ? (
				<Link to={{ pathname: `${panelScopePath}/pair/${pairId}/pair-details` }} className="flex-1-mhalf">
					<Box className="h-full" variant="white-bordered" slim full hoverable>
						<BoxSection contentClassName="justify-content-center align-items-center text-center">
							<span className="icon-container icon-container-lg">
								<IconSvg name="communication" className="text-primary" />
							</span>
							<span>{t('userPanel.dashboard.quickLinks.menteeDetails')}</span>
						</BoxSection>
					</Box>
				</Link>
			) : null}
			{secondStage ? (
				<>
					{panelType === 'mentor' ? (
						<Link
							to={{ pathname: `${panelScopePath}/pair/${pairId}/final-report` }}
							className="flex-1-mhalf"
						>
							<Box
								className="relative h-full"
								variant={hasFinalReportAlert ? 'white-rose-bordered' : 'white-bordered'}
								slim
								full
								hoverable
							>
								<BoxSection contentClassName="justify-content-center align-items-center text-center">
									<span className="icon-container icon-container-lg">
										<IconSvg name="checklist-square" className="text-primary" />
									</span>
									<span>{t('userPanel.dashboard.quickLinks.finalReport')}</span>
								</BoxSection>
								{hasFinalReportAlert ? (
									<div className="box-warning-icon">
										<Icon name="exclamation-circle" className="warning-icon-color" />
									</div>
								) : null}
							</Box>
						</Link>
					) : null}
					{panelType === 'mentee' ? (
						<Link
							to={{
								pathname: `${panelScopePath}/pair/${pairId}/session/last/evaluation-mentee`,
								hash: '#content',
							}}
							className="flex-1-mhalf"
						>
							<Box
								className="h-full"
								variant={hasEvaluationSurveyRequiredAlert ? 'white-rose-bordered' : 'white-bordered'}
								slim
								full
								hoverable
							>
								<BoxSection contentClassName="justify-content-center align-items-center text-center">
									<span className="icon-container icon-container-lg">
										<IconSvg name="arrow-circle" className="text-primary" />
									</span>
									<span>{t('userPanel.dashboard.quickLinks.goalsEvaluation')}</span>
								</BoxSection>
								{hasEvaluationSurveyRequiredAlert ? (
									<div className="box-warning-icon">
										<Icon name="exclamation-circle" className="warning-icon-color" />
									</div>
								) : null}
							</Box>
						</Link>
					) : null}
					<Link
						to={{ pathname: `${panelScopePath}/pair/${pairId}/process-summary` }}
						className="flex-1-mhalf"
					>
						<Box
							className="relative h-full"
							variant={hasProgressStepsAlert ? 'white-rose-bordered' : 'white-bordered'}
							slim
							full
							hoverable
						>
							<BoxSection contentClassName="justify-content-center align-items-center text-center">
								<span className="icon-container icon-container-lg">
									<IconSvg name="file-check" className="text-primary" />
								</span>
								<span>{t('userPanel.dashboard.quickLinks.processSummary')}</span>
							</BoxSection>
							{hasProgressStepsAlert ? (
								<div className="box-warning-icon">
									<Icon name="exclamation-circle" className="warning-icon-color" />
								</div>
							) : null}
						</Box>
					</Link>
				</>
			) : null}
		</div>
	);
};

export default MenteePairQuickLinks;
